import React from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronRightIcon,
  MapPinIcon,
  TruckIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  BuildingLibraryIcon,
  EnvelopeIcon,
  HashtagIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckSolid } from '@heroicons/react/24/solid';

import * as API from '../../api';
import PaymentContainer from '../../components/PaymentContainer';
import BookingSteps from '../../shared/components/BookingSteps';

/**
 * Order confirmation / thank-you page. Premium redesign.
 *
 * Logic preserved: reads order_response + AuthStorage (redirect home if
 * missing), calcPrices (total / discounted / advance / rest), roader lookup,
 * the deposit-vs-paid branch, the PaymentContainer pay-now flow, and the
 * bank-transfer details when no online deposit is due.
 */
const Thankyou = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const [settings, setSettings] = React.useState({});
  const order = JSON.parse(localStorage.getItem('order_response')) || {};
  // Read fresh — the user may have just authenticated inline on the booking
  // page, so the module-level API.AuthStorage snapshot can be stale/empty.
  const auth = JSON.parse(localStorage.getItem('auth')) || {};

  const [totalPrice, setTotalPrice] = React.useState(0);
  const [totalPriceReduc, setTotalPriceReduc] = React.useState(0);
  const [totalPricePay, setTotalPricePay] = React.useState(0);
  const [totalPriceRest, setTotalPriceRest] = React.useState(0);
  const [road, setRoad] = React.useState(null);

  const roaders = [
    { id: 1, value: 0, name: 'Je ne suis pas motorisé' },
    { id: 2, value: 3, name: 'Je suis motorisé, j’ai 3 places libres' },
    { id: 3, value: 2, name: 'Je suis motorisé, j’ai 2 places libres' },
    { id: 4, value: 1, name: 'Je suis motorisé, j’ai une place libres' },
    { id: 5, value: 0, name: 'Je suis motorisé, je n’ai aucune place libre' },
  ];

  const totalPlace = (_prices) => {
    let places = 0;
    (_prices || []).forEach((price) => {
      places = parseInt(places) + parseInt(price.qt ?? price.quantities);
    });
    return places;
  };

  const calcPrices = (o) => {
    let _totalPrice = 0;
    (o.prices || []).forEach((price) => {
      let p = parseInt(price.price) * parseInt(price.qt ?? price.quantities);
      _totalPrice = _totalPrice + p;
    });
    let _percentageSettings = 100 - o.percentage_paid;
    let _total =
      _totalPrice -
      (_totalPrice / 100) *
        (parseInt(o.coupon_value) ? parseInt(o.coupon_value) : parseInt(o.customer_discount));
    let _forPay = _total - (_total / 100) * _percentageSettings;
    let _forRest = _total - _forPay;
    setTotalPrice(_totalPrice);
    setTotalPriceReduc(_total);
    setTotalPricePay(_forPay);
    setTotalPriceRest(_forRest);
  };

  React.useEffect(() => {
    if (!Object.keys(order).length || !Object.keys(auth).length) {
      history.replace('/');
      return;
    }
    API.Settings().then((res) => setSettings(res.settings));
    calcPrices(order);
    if (roaders.some((r) => r.id == parseInt(order.roader))) {
      setRoad(roaders.find((r) => r.id == parseInt(order.roader)).name);
    }
    localStorage.removeItem('intended');
    document.title = `${API.APP_NAME} - ${t('thankyou.titlePaid')}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [0]);

  if (!Object.keys(order).length || !Object.keys(auth).length) return null;

  // Compare the numeric amount, not the formatted string: priceFormat uses a
  // non-breaking space so "0,00 MAD" string comparison is unreliable.
  const needsDeposit = Math.round(totalPricePay) > 0;
  // percentage_paid can be 100 (pay the full amount online via PayZone, not
  // a partial deposit) — the rest-to-pay rounds to 0 in that case, so calling
  // it a "deposit" with a "0,00 MAD balance due on site" line is misleading.
  const isFullOnlinePayment = needsDeposit && Math.round(totalPriceRest) <= 0;
  const email = auth?.user?.email;
  const guests = totalPlace(order.prices);
  const isSejour = order.check_in || order.check_out;
  const discountAmount = totalPriceReduc - totalPrice;

  return (
    <div className="bg-surface-muted pb-12">
      {/* Single unified header for all three payment states — breadcrumb,
          title, subtitle, step pills always in the same left-aligned
          position (matching Booking.js/checkout.js) regardless of whether
          payment is required. The centered checkmark + "Thank you!" block
          is its own section below the header, shown ONLY when no payment is
          needed (the booking is genuinely done at this point) — when
          payment is required, that block is skipped since the booking
          isn't finished yet. Step pills always render — 3 steps
          (Details/Payment/Confirmation) when payment applies, 2 steps
          (Details/Confirmation) when it doesn't. */}
      <header className="border-b border-slate-100 bg-surface">
        <div className="mx-auto w-full max-w-container px-4 py-7 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-1.5 text-xs text-ink-subtle" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-brand-500">
              {t('thankyou.breadcrumbHome')}
            </Link>
            <ChevronRightIcon className="h-3 w-3 rtl:rotate-180" />
            <span className="text-ink-muted">
              {needsDeposit ? t('booking.stepCheckout') : t('thankyou.stepDone')}
            </span>
          </nav>

          <h1 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
            {t('thankyou.titleDeposit')}
          </h1>
          <p className="mt-1 text-sm text-ink-muted">
            {isFullOnlinePayment
              ? t('thankyou.subtitlePaymentRequired')
              : needsDeposit
              ? t('thankyou.subtitleDeposit')
              : t('thankyou.subtitleConfirmed')}
          </p>

          {/* When no payment is needed, this page IS the final "Confirmation"
              step (2-step flow); when payment is needed, we're mid-flow at
              "Payment" (3-step flow) since the user still has to pay. */}
          <BookingSteps active={needsDeposit ? 'payment' : 'confirmation'} hasDeposit={needsDeposit} />
        </div>
      </header>

      <div className="mx-auto w-full max-w-container px-4 py-8 sm:px-6 lg:px-8">
        {/* Success confirmation — lives on the gray (bg-surface-muted) area
            with the summary cards below, not inside the white header, and
            only shown once there's nothing left to pay online. */}
        {needsDeposit ? null : (
          <div className="mb-8 flex flex-col items-center py-4 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
              <CheckSolid className="h-10 w-10 text-emerald-500" />
            </span>
            <p className="mt-5 max-w-xl font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">
              {t('thankyou.titlePaid')}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_380px]">
          {/* Left: recap + bank details — shown second on mobile so the
              Order details / Pay now card (right) surfaces first. */}
          <div className="order-2 space-y-6 lg:order-1">
            <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-card sm:p-6">
              <div className="grid grid-cols-1 gap-px overflow-hidden rounded-xl bg-slate-100 sm:grid-cols-2">
                <RecapCell icon={HashtagIcon} label={t('thankyou.orderNumber')} value={`#${order.id}`} />
                <RecapCell icon={EnvelopeIcon} label={t('thankyou.email')} value={email} />
                <RecapCell icon={CreditCardIcon} label={t('thankyou.total')} value={API.priceFormat(totalPrice)} />
                <RecapCell
                  icon={BuildingLibraryIcon}
                  label={t('thankyou.paymentMethod')}
                  value={
                    // Three cases, not two: the old ternary showed "À payer sur place"
                    // on exactly the orders that require a card payment.
                    isFullOnlinePayment
                      ? t('thankyou.methodCard')
                      : needsDeposit
                        ? t('thankyou.methodCardDeposit')
                        : t('thankyou.methodBank')
                  }
                />
              </div>
            </section>

            {/* Bank details (only for bank-transfer orders) */}
            {!needsDeposit ? (
              <section className="rounded-2xl border border-brand-100 bg-brand-50/50 p-5 shadow-card sm:p-6">
                <div className="mb-4 flex items-center gap-2">
                  <BuildingLibraryIcon className="h-5 w-5 text-brand-500" />
                  <h2 className="font-display text-lg font-semibold text-ink">{t('thankyou.bankTitle')}</h2>
                </div>
                <dl className="space-y-2.5 text-sm">
                  <BankRow label={t('thankyou.bankName')} value={settings.bank_name} />
                  <BankRow label={t('thankyou.bankHolder')} value={settings.full_name} />
                  <BankRow label={t('thankyou.bankRib')} value={settings.rib} mono />
                </dl>
              </section>
            ) : null}

            <Link
              to="/account/orders"
              className="inline-flex items-center gap-2 rounded-full border border-brand-500 px-5 py-2.5 text-sm font-semibold text-brand-500 transition-colors hover:bg-brand-500 hover:text-white"
            >
              {t('thankyou.viewBookings')}
              <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5 rtl:rotate-180" aria-hidden>
                <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z" clipRule="evenodd" />
              </svg>
            </Link>
          </div>

          {/* Right: order summary — shown first on mobile */}
          <aside className="order-1 lg:order-2">
            <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-card lg:sticky lg:top-24">
              <div className="border-b border-slate-100 px-5 py-4">
                <h3 className="font-display text-base font-semibold text-ink">{t('thankyou.summaryTitle')}</h3>
              </div>

              <div className="space-y-2.5 px-5 py-4 text-sm">
                {isSejour ? (
                  <>
                    {order.check_in ? <Row icon={CalendarDaysIcon} label={t('thankyou.checkIn')} value={order.check_in} /> : null}
                    {order.check_out ? <Row icon={CalendarDaysIcon} label={t('thankyou.checkOut')} value={order.check_out} /> : null}
                  </>
                ) : (
                  <Row icon={CalendarDaysIcon} label={t('thankyou.date')} value={new Date(order.created_at).toLocaleDateString('fr-FR')} />
                )}
                {order.depart_city && order.depart_city !== '1' ? (
                  <Row icon={MapPinIcon} label={t('thankyou.departureCity')} value={order.depart_city} />
                ) : null}
                {road ? <Row icon={TruckIcon} label={t('thankyou.carpool')} value={road} /> : null}
                <Row icon={UserGroupIcon} label={t('thankyou.guests')} value={guests} />
              </div>

              <div className="space-y-2 border-t border-slate-100 px-5 py-4 text-sm">
                <div className="flex items-center justify-between text-ink-muted">
                  <span>{t('thankyou.subtotal')}</span>
                  <bdi>{API.priceFormat(totalPrice)}</bdi>
                </div>
                {discountAmount !== 0 ? (
                  <div className="flex items-center justify-between text-emerald-600">
                    <span>{t('thankyou.discount')}</span>
                    <bdi>{API.priceFormat(discountAmount)}</bdi>
                  </div>
                ) : null}
                <div className="flex items-center justify-between pt-1">
                  <span className="font-display text-base font-semibold text-ink">{t('thankyou.total')}</span>
                  <bdi className="font-display text-xl font-bold text-brand-500">{API.priceFormat(totalPriceReduc)}</bdi>
                </div>
                {needsDeposit ? (
                  <div className="mt-2 space-y-3 rounded-xl bg-surface-muted p-3 text-xs">
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-ink">
                        <span>{isFullOnlinePayment ? t('thankyou.amountToPay') : t('thankyou.advanceToPay')}</span>
                        <bdi className="font-semibold">{API.priceFormat(totalPricePay)}</bdi>
                      </div>
                      {isFullOnlinePayment ? null : (
                        <div className="flex items-center justify-between text-ink-subtle">
                          <span>{t('thankyou.restToPay')}</span>
                          <bdi>{API.priceFormat(totalPriceRest)}</bdi>
                        </div>
                      )}
                    </div>
                    <PaymentContainer buttonText={t('thankyou.payNow')} size="lg" style={{ width: '100%', justifyContent: 'center' }} />
                  </div>
                ) : null}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

// ---- helpers ----------------------------------------------------------------

function RecapCell({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 bg-white px-4 py-3.5">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-500">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-ink-subtle">{label}</p>
        <p className="truncate text-sm font-semibold text-ink">{value}</p>
      </div>
    </div>
  );
}

function BankRow({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-ink-muted">{label}</dt>
      <dd className={`font-medium text-ink ${mono ? 'font-mono text-xs' : ''}`}>{value || '—'}</dd>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 text-ink-muted">
      <span className="inline-flex items-center gap-2">
        <Icon className="h-4 w-4 text-brand-500" />
        {label}
      </span>
      <span className="text-right font-medium text-ink rtl:text-left">{value}</span>
    </div>
  );
}

export default Thankyou;
